import { PRE_LOAD_CONFIGURATIONS } from "Constants/permissionConstants";
import { ROLE_VISITOR, STAFF } from "Constants/stringConstants";
import _ from "lodash";
import { readConfigurationFragmentV2 } from "modules/CommonGraphqlHelpers";
import { getUserPrimaryGradeId } from "modules/CommonSelectors";
import { getJsonItem } from "services/localStorage";
import { parseConfigurationValue } from "Utils/Common.js";
import { getIsModuleSettingsV2Enabled } from "Utils/ACLStoreUtils";
import { isPublicPlatformRoute } from "../RouteMatchUtils";
import {
  getUserInfo,
  fetchOrganizationAndUserIdForPublicPost,
} from "../UserDetailUtils";
import { getConfigurationsFromCache } from "./GraphqlHelpers";

const CONFIGURATION_ENTITY_LIST = {
  ORGANIZATION: ["ORGANIZATION", "USER"],
  CURRICULUM: ["CURRICULUM_PROGRAM", "USER"],
  CURRICULUM_AND_GRADE: ["CURRICULUM_PROGRAM", "GRADE", "USER"],
  COURSE: ["COURSE", "USER"],
  ORGANIZATION_ONLY: ["ORGANIZATION"],
  CURRICULUM_ONLY: ["CURRICULUM_PROGRAM"],
  COURSE_ONLY: ["COURSE"],
  USER_ONLY: ["USER"],
  IDENTITY: ["IDENTITY", "ORGANIZATION"],
  ORGANIZATION_WITH_USER_AND_IDENTITY: ["ORGANIZATION", "USER", "IDENTITY"],
  GRADE_ONLY: ["GRADE"],
  IDENTITY_ONLY: ["IDENTITY"],
  LEARNING_COURSE: ["LEARNING_COURSE", "USER"],
  CURRICULUM_WITH_ACADEMIC_YEAR: ["CURRICULUM_PROGRAM", "ACADEMIC_YEAR"],
};

const getEntityIdBasedOnEntityType = ({
  entityType,
  userId,
  currentCurriculumProgramId,
  academicYearId,
  courseId,
  organizationId,
  hasChildContext,
  childId,
  identityId,
  gradeId,
  portalType = "PLANNER",
  learningCourseId,
}) => {
  const userInfoLocal = getUserInfo({ portalType });
  switch (entityType) {
    case "ORGANIZATION":
      return organizationId || userInfoLocal.org_id;
    case "USER": {
      const isParentLogin = _.get(userInfoLocal, "user_type") === "parent";
      const shouldUseChildId = isParentLogin && hasChildContext;
      const userIdFromParam = shouldUseChildId ? childId : userId;
      const defaultUserId = shouldUseChildId
        ? userInfoLocal.childID
        : userInfoLocal.id;

      return userIdFromParam || defaultUserId;
    }
    case "CURRICULUM_PROGRAM":
      return (
        currentCurriculumProgramId ||
        _.get(getJsonItem("currentCurriculumProgram"), "id")
      );
    case "COURSE":
      return courseId;
    case "IDENTITY":
      return identityId;
    case "GRADE":
      return gradeId || getUserPrimaryGradeId();
    case "LEARNING_COURSE":
      return learningCourseId;
    case "ACADEMIC_YEAR":
      return academicYearId;
  }
};

const getConfigurationEntityGroups = ({
  userData,
  entityTypes,
  hasChildContext,
  portalType,
}) => {
  return _.map(entityTypes, entityType => {
    const entityId = getEntityIdBasedOnEntityType({
      entityType,
      hasChildContext,
      portalType,
      ...userData,
    });
    return { entityId, entityType };
  });
};

const getConfigurationContexts = ({ src, metadata }) => {
  const {
    portalType = "PLANNER",
    appType,
    moduleName,
    isAdminNotificationManager,
    courseId,
    currentCurriculumProgramId,
    userId,
    childId,
    organizationId,
    learningCourseId,
  } = metadata;

  const userInfo = getUserInfo();

  const userType = userInfo.user_type;

  let identityId =
    metadata.identityId || userInfo.identityId || userInfo.identity_id;

  identityId = _.toString(identityId);

  const gradeId = metadata?.gradeId || getUserPrimaryGradeId();

  const groupInput = {
    currentCurriculumProgramId,
    courseId,
    userId: isPublicPlatformRoute()
      ? fetchOrganizationAndUserIdForPublicPost().userId
      : userId,
    childId,
    identityId,
    gradeId,
    organizationId: isPublicPlatformRoute()
      ? fetchOrganizationAndUserIdForPublicPost().organizationId
      : organizationId,
    learningCourseId,
  };

  let entityTypes = [];

  const hasChildContext = _.includes(
    ["CURRICULUM_LEVEL", "COURSE_LEVEL", "LEARNING_COURSE_LEVEL"],
    src
  ); // read child id for COURSE & CURRICULUM context only, otherwise normal userId should be used

  switch (src) {
    case "ORGANIZATION_LEVEL":
      // For staff, we can fetch identity level configurations along with (user + organization) level configurations
      entityTypes =
        !_.isEmpty(identityId) &&
        _.isEqual(userType, STAFF) &&
        !_.isEqual(portalType, "COMMUNITY")
          ? CONFIGURATION_ENTITY_LIST.ORGANIZATION_WITH_USER_AND_IDENTITY
          : CONFIGURATION_ENTITY_LIST.ORGANIZATION;
      break;
    case "IDENTITY_LEVEL":
      entityTypes = CONFIGURATION_ENTITY_LIST.IDENTITY;
      break;
    case "PARENT_AND_CURRICULUM_LEVEL":
    case "CURRICULUM_LEVEL":
      entityTypes = CONFIGURATION_ENTITY_LIST.CURRICULUM;

      if (getIsModuleSettingsV2Enabled() && userType !== STAFF) {
        entityTypes = CONFIGURATION_ENTITY_LIST.CURRICULUM_AND_GRADE;
      }

      break;
    case "PARENT_AND_COURSE_LEVEL":
      entityTypes = CONFIGURATION_ENTITY_LIST.COURSE;
      break;
    case "COURSE_LEVEL":
      entityTypes = CONFIGURATION_ENTITY_LIST.COURSE;
      break;
    case "SETTINGS_PAGE":
      if (appType === "ADMIN_WEB") {
        if (
          _.includes(
            [
              "TURNITIN",
              "USER_NAME_SETTING",
              "USER_NAME_SETTING_V2",
              "PRINT_NAME_SETTINGS",
            ],
            moduleName
          )
        )
          entityTypes = CONFIGURATION_ENTITY_LIST.ORGANIZATION;
        else
          entityTypes = isAdminNotificationManager
            ? CONFIGURATION_ENTITY_LIST.CURRICULUM_ONLY
            : CONFIGURATION_ENTITY_LIST.CURRICULUM;
        // curriculum settings
      } else
        entityTypes = courseId
          ? CONFIGURATION_ENTITY_LIST.COURSE_ONLY // Class setting
          : CONFIGURATION_ENTITY_LIST.USER_ONLY; // notification settings
      break;
    case "MODULE_SETTINGS_V2":
      entityTypes = _.isEqual(moduleName, "PROGRAM_MODULE_SETTINGS_V2")
        ? CONFIGURATION_ENTITY_LIST.CURRICULUM_ONLY
        : CONFIGURATION_ENTITY_LIST.ORGANIZATION_ONLY;
      break;
    case "SETTINGS_AND_TEACHER_PERMISSION":
      entityTypes = CONFIGURATION_ENTITY_LIST.CURRICULUM;
      break;
    case "TEACHER_PERMISSION":
      entityTypes = CONFIGURATION_ENTITY_LIST.CURRICULUM;
      break;
    case "ADMIN_PORTAL_HOME_PAGE":
      entityTypes = CONFIGURATION_ENTITY_LIST.CURRICULUM;

      break;
    case "COURSE_ONLY_LEVEL":
      entityTypes = CONFIGURATION_ENTITY_LIST.COURSE_ONLY;
      break;
    case "GRADE_ONLY":
      entityTypes = CONFIGURATION_ENTITY_LIST.GRADE_ONLY;
      break;
    case "IDENTITY_ONLY":
      entityTypes = CONFIGURATION_ENTITY_LIST.IDENTITY;
      break;
    case "LEARNING_COURSE_LEVEL":
      entityTypes = CONFIGURATION_ENTITY_LIST.LEARNING_COURSE;
      break;
    case "PARENT_AND_LEARNING_COURSE_LEVEL":
      entityTypes = CONFIGURATION_ENTITY_LIST.LEARNING_COURSE;
      break;
    case "HOME_PAGE":
      entityTypes = CONFIGURATION_ENTITY_LIST.CURRICULUM;
      break;
  }

  return getConfigurationEntityGroups({
    userData: groupInput,
    entityTypes,
    hasChildContext,
    portalType,
  });
};

const generateConfigurationQueryVariables = params => {
  const {
    src,
    organizationId,
    currentCurriculumProgramId,
    courseId,
    childId,
    curriculumProgramList,
    appType,
    moduleName,
    userId,
    portalType = "PLANNER",
    fetchInitialLoadConfigurations = false,
    configurations = [],
    isCourseModuleSetting = false,
    isAdminNotificationManager = false,
    learningCourseId,
    modules,
  } = params;

  const userInfo = getUserInfo();
  let userType = userInfo.user_type;
  let identityId =
    params.identityId || userInfo.identityId || userInfo.identity_id;

  identityId = _.toString(identityId);

  const gradeId = params?.gradeId || getUserPrimaryGradeId();

  let entityTypes,
    configurationGroups,
    excludedConfigurationGroups,
    isAdminPage = false,
    isSettingsPage = false,
    _appType,
    excludeInterRepoConfigurations = true;

  const groupInput = {
    currentCurriculumProgramId,
    courseId,
    userId: isPublicPlatformRoute()
      ? fetchOrganizationAndUserIdForPublicPost().userId
      : userId,
    childId,
    identityId,
    gradeId,
    organizationId: isPublicPlatformRoute()
      ? fetchOrganizationAndUserIdForPublicPost().organizationId
      : organizationId,
    learningCourseId,
  };

  const isVisitor = _.isEqual(userInfo.role, ROLE_VISITOR);
  if (isVisitor) {
    userType = ROLE_VISITOR;
  }
  let groupInputList = [groupInput];

  const preloadedConfigurations = _.get(
    PRE_LOAD_CONFIGURATIONS,
    [userType, src],
    []
  );

  const hasChildContext = _.includes(
    ["CURRICULUM_LEVEL", "COURSE_LEVEL", "LEARNING_COURSE_LEVEL"],
    src
  ); // read child id for COURSE & CURRICULUM context only, otherwise normal userId should be used

  switch (src) {
    case "ORGANIZATION_LEVEL":
      // For staff, we can fetch identity level configurations along with (user + organization) level configurations
      entityTypes =
        !_.isEmpty(identityId) &&
        _.isEqual(userType, STAFF) &&
        !_.isEqual(portalType, "COMMUNITY")
          ? CONFIGURATION_ENTITY_LIST.ORGANIZATION_WITH_USER_AND_IDENTITY
          : CONFIGURATION_ENTITY_LIST.ORGANIZATION;
      break;
    case "IDENTITY_LEVEL":
      configurationGroups = ["IdentityAccess"];
      entityTypes = CONFIGURATION_ENTITY_LIST.IDENTITY;
      break;
    case "PARENT_AND_CURRICULUM_LEVEL":
    case "CURRICULUM_LEVEL":
      entityTypes = CONFIGURATION_ENTITY_LIST.CURRICULUM;

      if (getIsModuleSettingsV2Enabled() && userType !== STAFF) {
        entityTypes = CONFIGURATION_ENTITY_LIST.CURRICULUM_AND_GRADE;
      }

      break;
    case "PARENT_AND_COURSE_LEVEL":
      configurationGroups = ["SETTING"];
      entityTypes = CONFIGURATION_ENTITY_LIST.COURSE;
      break;
    case "COURSE_LEVEL":
      entityTypes = CONFIGURATION_ENTITY_LIST.COURSE;
      break;
    case "SETTINGS_PAGE":
      configurationGroups = ["SETTING", "STATE_ROOT"];
      isSettingsPage = true;
      _appType = appType;
      excludeInterRepoConfigurations = false;

      if (appType === "ADMIN_WEB") {
        if (
          _.includes(
            [
              "TURNITIN",
              "USER_NAME_SETTING",
              "USER_NAME_SETTING_V2",
              "PRINT_NAME_SETTINGS",
            ],
            moduleName
          )
        )
          entityTypes = CONFIGURATION_ENTITY_LIST.ORGANIZATION;
        else
          entityTypes = isAdminNotificationManager
            ? CONFIGURATION_ENTITY_LIST.CURRICULUM_ONLY
            : CONFIGURATION_ENTITY_LIST.CURRICULUM;
        // curriculum settings
      } else
        entityTypes = courseId
          ? CONFIGURATION_ENTITY_LIST.COURSE_ONLY // Class setting
          : CONFIGURATION_ENTITY_LIST.USER_ONLY; // notification settings
      break;
    case "MODULE_SETTINGS_V2":
      configurationGroups = ["SETTING", "STATE_ROOT"];
      isSettingsPage = true;
      _appType = appType;
      excludeInterRepoConfigurations = false;
      entityTypes = _.isEqual(moduleName, "PROGRAM_MODULE_SETTINGS_V2")
        ? CONFIGURATION_ENTITY_LIST.CURRICULUM_ONLY
        : CONFIGURATION_ENTITY_LIST.ORGANIZATION_ONLY;
      if (!_.isEmpty(curriculumProgramList)) {
        groupInputList = _.map(curriculumProgramList, program => ({
          currentCurriculumProgramId: program.id,
        }));
      }
      break;
    case "SETTINGS_AND_TEACHER_PERMISSION":
      configurationGroups = ["SETTING", "STATE_ROOT", "StaffPermission"];
      entityTypes = CONFIGURATION_ENTITY_LIST.CURRICULUM;
      break;
    case "TEACHER_PERMISSION":
      entityTypes = CONFIGURATION_ENTITY_LIST.CURRICULUM;
      configurationGroups = ["StaffPermission", "LibraryPortal"];
      groupInputList = _.map(curriculumProgramList, program => ({
        currentCurriculumProgramId: program.id,
      }));
      break;
    case "ADMIN_PORTAL_HOME_PAGE":
      entityTypes = CONFIGURATION_ENTITY_LIST.CURRICULUM;
      configurationGroups = ["AdminPortal", "Common", "SETTING"];
      groupInputList = _.map(curriculumProgramList, program => ({
        currentCurriculumProgramId: program.id,
      }));
      isAdminPage = true;
      break;
    case "COURSE_ONLY_LEVEL":
      entityTypes = CONFIGURATION_ENTITY_LIST.COURSE_ONLY;
      break;
    case "GRADE_ONLY":
      entityTypes = CONFIGURATION_ENTITY_LIST.GRADE_ONLY;
      break;
    case "IDENTITY_ONLY":
      entityTypes = CONFIGURATION_ENTITY_LIST.IDENTITY;
      configurationGroups = ["IdentityAccess"];
      break;
    case "LEARNING_COURSE_LEVEL":
      entityTypes = CONFIGURATION_ENTITY_LIST.LEARNING_COURSE;
      break;
    case "PARENT_AND_LEARNING_COURSE_LEVEL":
      configurationGroups = ["SETTING"];
      entityTypes = CONFIGURATION_ENTITY_LIST.LEARNING_COURSE;
      break;
    case "HOME_PAGE":
      entityTypes = CONFIGURATION_ENTITY_LIST.CURRICULUM;
      groupInputList = _.map(curriculumProgramList, program => ({
        currentCurriculumProgramId: program.id,
      }));
      break;
  }

  if (portalType === "COMMUNITY")
    configurationGroups = ["CommunityPortal", "LibraryPortal"];

  const configurationEntityGroupsList = _.map(groupInputList, groupInput =>
    getConfigurationEntityGroups({
      userData: groupInput,
      entityTypes,
      hasChildContext,
      portalType,
    })
  );

  const configurationEntityGroupFilters = _.map(
    configurationEntityGroupsList,
    configurationEntityGroups => ({
      configurationEntityGroups,
      configurationGroups,
      excludedConfigurationGroups,
      appType: _appType, // send appType only for queries from setting pages
      excludeInterRepoConfigurations,
      configurations: !_.isEmpty(configurations)
        ? configurations
        : fetchInitialLoadConfigurations
        ? preloadedConfigurations
        : [],
      ...(modules && { modules }),
    })
  );

  return {
    configurationEntityGroupFilters,
    isAdminPage,
    isSettingsPage,
    portalType,
    isCourseModuleSetting,
    isAdminNotificationManager,
  };
};

const generateConfigurationQueryVariablesV2 = params => {
  const {
    src,
    organizationId,
    currentCurriculumProgramId,
    courseId,
    childId,
    curriculumProgramList,
    appType,
    moduleName,
    userId,
    portalType = "PLANNER",
    fetchInitialLoadConfigurations = false,
    configurations = [],
    isCourseModuleSetting = false,
    isAdminNotificationManager = false,
    learningCourseId,
    modules,
  } = params;

  const userInfo = getUserInfo();
  let userType = userInfo.user_type;
  let identityId =
    params.identityId || userInfo.identityId || userInfo.identity_id;

  identityId = _.toString(identityId);

  const gradeId = params?.gradeId || getUserPrimaryGradeId();

  let entityTypes = [],
    excludedConfigurationGroups,
    isSettingsPage = false,
    _appType,
    excludeInterRepoConfigurations = true;

  const groupInput = {
    currentCurriculumProgramId,
    courseId,
    userId: isPublicPlatformRoute()
      ? fetchOrganizationAndUserIdForPublicPost().userId
      : userId,
    childId,
    identityId,
    gradeId,
    organizationId: isPublicPlatformRoute()
      ? fetchOrganizationAndUserIdForPublicPost().organizationId
      : organizationId,
    learningCourseId,
  };

  const isVisitor = _.isEqual(userInfo.role, ROLE_VISITOR);
  if (isVisitor) {
    userType = ROLE_VISITOR;
  }
  let groupInputList = [groupInput];

  const preloadedConfigurations = _.get(
    PRE_LOAD_CONFIGURATIONS,
    [userType, src],
    []
  );

  const hasChildContext = _.includes(
    ["CURRICULUM_LEVEL", "COURSE_LEVEL", "LEARNING_COURSE_LEVEL"],
    src
  ); // read child id for COURSE & CURRICULUM context only, otherwise normal userId should be used

  switch (src) {
    case "ORGANIZATION_LEVEL":
      // For staff, we can fetch identity level configurations along with (user + organization) level configurations
      entityTypes = [
        !_.isEmpty(identityId) &&
        _.isEqual(userType, STAFF) &&
        !_.isEqual(portalType, "COMMUNITY")
          ? CONFIGURATION_ENTITY_LIST.ORGANIZATION_WITH_USER_AND_IDENTITY
          : CONFIGURATION_ENTITY_LIST.ORGANIZATION,
      ];
      break;
    case "IDENTITY_LEVEL":
      entityTypes = [CONFIGURATION_ENTITY_LIST.IDENTITY];
      break;
    case "PARENT_AND_CURRICULUM_LEVEL":
    case "CURRICULUM_LEVEL":
      entityTypes = [CONFIGURATION_ENTITY_LIST.CURRICULUM];

      if (getIsModuleSettingsV2Enabled() && userType !== STAFF) {
        entityTypes = [CONFIGURATION_ENTITY_LIST.CURRICULUM_AND_GRADE];
      }

      break;
    case "PARENT_AND_COURSE_LEVEL":
      entityTypes = [CONFIGURATION_ENTITY_LIST.COURSE];
      break;
    case "COURSE_LEVEL":
      entityTypes = [CONFIGURATION_ENTITY_LIST.COURSE];
      break;
    case "SETTINGS_PAGE":
      isSettingsPage = true;
      _appType = appType;
      excludeInterRepoConfigurations = false;

      if (appType === "ADMIN_WEB") {
        if (
          _.includes(
            [
              "TURNITIN",
              "USER_NAME_SETTING",
              "USER_NAME_SETTING_V2",
              "PRINT_NAME_SETTINGS",
            ],
            moduleName
          )
        )
          entityTypes = [CONFIGURATION_ENTITY_LIST.ORGANIZATION];
        else
          entityTypes = isAdminNotificationManager
            ? [CONFIGURATION_ENTITY_LIST.CURRICULUM_ONLY]
            : [CONFIGURATION_ENTITY_LIST.CURRICULUM];
        // curriculum settings
      } else
        entityTypes = courseId
          ? [CONFIGURATION_ENTITY_LIST.COURSE_ONLY] // Class setting
          : [CONFIGURATION_ENTITY_LIST.USER_ONLY]; // notification settings
      break;
    case "MODULE_SETTINGS_V2":
      isSettingsPage = true;
      _appType = appType;
      excludeInterRepoConfigurations = false;
      entityTypes = _.isEqual(moduleName, "PROGRAM_MODULE_SETTINGS_V2")
        ? [CONFIGURATION_ENTITY_LIST.CURRICULUM_ONLY]
        : [CONFIGURATION_ENTITY_LIST.ORGANIZATION_ONLY];
      if (
        !_.isEmpty(curriculumProgramList) &&
        moduleName !== "PROGRAM_MODULE_SETTINGS_V2"
      ) {
        groupInputList = _.map(curriculumProgramList, program => ({
          currentCurriculumProgramId: program.id,
        }));

        entityTypes = [];

        _.forEach(curriculumProgramList, program => {
          entityTypes.push(CONFIGURATION_ENTITY_LIST.CURRICULUM_ONLY);
        });
      }
      break;
    case "SETTINGS_AND_TEACHER_PERMISSION":
      entityTypes = [CONFIGURATION_ENTITY_LIST.CURRICULUM];
      break;
    case "TEACHER_PERMISSION": {
      groupInputList = _.map(curriculumProgramList, program => ({
        currentCurriculumProgramId: program.id,
      }));

      entityTypes = [];

      _.forEach(curriculumProgramList, program => {
        entityTypes.push(CONFIGURATION_ENTITY_LIST.CURRICULUM_ONLY);
      });
      break;
    }
    case "HOME_PAGE":
    case "ALL_SCHOOL_PROGRAMS":
    case "ADMIN_PORTAL_HOME_PAGE": {
      groupInputList = _.map(curriculumProgramList, program => ({
        currentCurriculumProgramId: program.id,
      }));

      entityTypes = [];

      _.forEach(curriculumProgramList, program => {
        entityTypes.push(CONFIGURATION_ENTITY_LIST.CURRICULUM);
      });
      break;
    }
    case "COURSE_ONLY_LEVEL":
      entityTypes = [CONFIGURATION_ENTITY_LIST.COURSE_ONLY];
      break;
    case "GRADE_ONLY":
      entityTypes = [CONFIGURATION_ENTITY_LIST.GRADE_ONLY];
      break;
    case "IDENTITY_ONLY":
      entityTypes = [CONFIGURATION_ENTITY_LIST.IDENTITY];
      break;
    case "LEARNING_COURSE_LEVEL":
      entityTypes = [CONFIGURATION_ENTITY_LIST.LEARNING_COURSE];
      break;
    case "PARENT_AND_LEARNING_COURSE_LEVEL":
      entityTypes = [CONFIGURATION_ENTITY_LIST.LEARNING_COURSE];
      break;
    case "ORG_CURRICULUM_LEARNING_COURSE_LEVEL":
      entityTypes = [
        CONFIGURATION_ENTITY_LIST.ORGANIZATION_WITH_USER_AND_IDENTITY,
        CONFIGURATION_ENTITY_LIST.CURRICULUM,
        CONFIGURATION_ENTITY_LIST.LEARNING_COURSE,
      ];
      groupInputList = [groupInput, groupInput, groupInput];
      break;
    case "ORG_CURRICULUM_LEVEL":
      entityTypes = [
        CONFIGURATION_ENTITY_LIST.ORGANIZATION_WITH_USER_AND_IDENTITY,
        CONFIGURATION_ENTITY_LIST.CURRICULUM,
      ];
      groupInputList = [groupInput, groupInput];
      break;
    case "ORG_CURRICULUM_COURSE_LEVEL":
      entityTypes = [
        CONFIGURATION_ENTITY_LIST.ORGANIZATION_WITH_USER_AND_IDENTITY,
        CONFIGURATION_ENTITY_LIST.CURRICULUM,
        CONFIGURATION_ENTITY_LIST.COURSE,
      ];
      groupInputList = [groupInput, groupInput, groupInput];
      break;
    case "ORG_CURRICULUM_LEARNING_COURSE_CLASS_LEVEL":
      entityTypes = [
        CONFIGURATION_ENTITY_LIST.ORGANIZATION_WITH_USER_AND_IDENTITY,
        CONFIGURATION_ENTITY_LIST.CURRICULUM,
        CONFIGURATION_ENTITY_LIST.LEARNING_COURSE,
        CONFIGURATION_ENTITY_LIST.COURSE,
      ];
      groupInputList = [groupInput, groupInput, groupInput, groupInput];
      break;
  }

  const configurationEntityGroupsList = _.map(
    groupInputList,
    (groupInput, index) =>
      getConfigurationEntityGroups({
        userData: groupInput,
        entityTypes: entityTypes[index],
        hasChildContext,
        portalType,
      })
  );

  const configurationEntityGroupFilters = _.map(
    configurationEntityGroupsList,
    configurationEntityGroups => ({
      configurationEntityGroups,
      excludedConfigurationGroups,
      appType: _appType, // send appType only for queries from setting pages
      excludeInterRepoConfigurations,
      configurations: !_.isEmpty(configurations)
        ? configurations
        : fetchInitialLoadConfigurations
        ? preloadedConfigurations
        : [],
      ...(modules && { modules }),
    })
  );

  return {
    configurationEntityGroupFilters,
    isSettingsPage,
    portalType,
    isCourseModuleSetting,
    isAdminNotificationManager,
  };
};

const getConfigurationValue = inputProps => {
  try {
    const { configName, readFromUserAndOrgLevel } = inputProps;

    const CONFIGURATION_READ_PRECEDENCE = [
      "GRADE_ONLY",
      "PARENT_AND_COURSE_LEVEL",
      "COURSE_LEVEL",
      "PARENT_AND_LEARNING_COURSE_LEVEL",
      "LEARNING_COURSE_LEVEL",
      "PARENT_AND_CURRICULUM_LEVEL",
      "CURRICULUM_LEVEL",
      "ORGANIZATION_LEVEL",
    ];
    const PRECEDENCE_LEVELS = readFromUserAndOrgLevel
      ? ["ORGANIZATION_LEVEL"]
      : CONFIGURATION_READ_PRECEDENCE;

    const getConfigurationValueBasedOnPrecedence = () => {
      let value;
      for (const src of PRECEDENCE_LEVELS) {
        const configurationVariables = generateConfigurationQueryVariables({
          ...inputProps,
          src,
        });

        const configuration = readConfigurationFragmentV2(
          configurationVariables,
          configName
        );

        const configurationValue = parseConfigurationValue(configuration);
        if (!_.isNil(configurationValue)) {
          value = configurationValue;
          break;
        }
      }
      return value;
    };

    const val = getConfigurationValueBasedOnPrecedence();
    return val;
  } catch (err) {
    console.error(err);
  }
};

const getSettingValue = ({
  name,
  courseId,
  organizationId,
  userId,
  readFromUserAndOrgLevel,
  currentCurriculumProgramId,
  portalType = "PLANNER",
  gradeId,
  learningCourseId,
}) => {
  return getConfigurationValue({
    configName: name,
    courseId,
    organizationId,
    userId,
    readFromUserAndOrgLevel,
    currentCurriculumProgramId,
    portalType,
    gradeId,
    learningCourseId,
  });
};

const getConfigurationsForGivenContextFromMultipleContexts = ({
  variables,
  entityId: entityIdParam,
  entityType: entityTypeParam,
}) => {
  const configurationsList = getConfigurationsFromCache(variables);

  const configurations = _.get(
    _.find(configurationsList, ({ entityGroups }) => {
      return _.some(entityGroups, ({ entityId, entityType }) => {
        return entityId === entityIdParam && entityType === entityTypeParam;
      });
    }),
    "configurations",
    []
  );

  return configurations;
};

const getConfigurationAdditionalVariables = ({
  src,
  isCourseModuleSetting = false,
  isAdminNotificationManager = false,
}) => {
  const isSettingsPage =
    src === "SETTINGS_PAGE" || src === "MODULE_SETTINGS_V2";

  return {
    isSettingsPage,
    isCourseModuleSetting,
    isAdminNotificationManager,
  };
};

export {
  getConfigurationValue,
  getSettingValue,
  generateConfigurationQueryVariables,
  generateConfigurationQueryVariablesV2,
  getConfigurationContexts,
  getConfigurationAdditionalVariables,
  getConfigurationsForGivenContextFromMultipleContexts,
};
